import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { LabelAndValue } from '@/types/label-and-value'
import type { DeepCamelCase } from '@/types/utils'
import { parseNameAndIdToLabelAndValue } from '@/utils/parse-id-and-name-to-label-and-value'
import { requiredString } from '@/utils/zod'

type Response = z.infer<typeof schema>

const schema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    short_name: requiredString()
  })
)

type EducationLevel = DeepCamelCase<Response>[number]

const parseToMap = (response: Response): Record<string, EducationLevel> =>
  response.reduce(
    (acc, level) => ({
      ...acc,
      [level.id]: {
        id: level.id,
        name: level.name,
        shortName: level.short_name
      }
    }),
    {}
  )

const parseToOptions = (response: Response): LabelAndValue[] =>
  response.map(parseNameAndIdToLabelAndValue)

export const useAllEducationLevels = () =>
  useQuery({
    queryKey: ['panelEducationLevelsListAll'],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelEducationLevelsList({
        page_size: 'max',
        fetchKeys: {
          id: true,
          name: true,
          short_name: true
        }
      })

      const parsed = schema.parse(response.results)

      return {
        options: parseToOptions(parsed),
        map: parseToMap(parsed)
      }
    }
  })
